import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Award, Languages, Star, ShieldCheck, ArrowRight, Zap,
  BadgeCheck, Users, Clock, MessageCircle, Phone, Stethoscope, Share2, Quote, Check,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoOf = (d) => {
  if (!d?.photo_url) return null;
  return d.photo_url.startsWith("http") ? d.photo_url : `${BACKEND}${d.photo_url}`;
};

export default function DoctorProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [reviewsBlock, setReviewsBlock] = useState({ items: [], avg_rating: 0, review_count: 0 });
  const [shared, setShared] = useState(false);

  useEffect(() => {
    api.get(`/doctors/${id}`).then(r => setDoc(r.data)).catch(() => toast.error("Doctor not found"));
    api.get("/doctors").then(r => setDoctors(r.data));
    api.get(`/doctors/${id}/reviews?limit=6`).then(r => setReviewsBlock(r.data || { items: [] })).catch(() => {});
  }, [id]);

  // Update document title + meta description for SEO when doctor loads.
  // Guard against double-honorific ("Dr. Dr. X") when the DB name already
  // starts with "Dr." — the founder's account does — and use the correct
  // `specialization` field (backend never returns `specialty`).
  const withDr = (n) => (!n ? "" : /^dr\.?\s/i.test(n.trim()) ? n : `Dr. ${n}`);
  // Extract the actual given name (skip "Dr." / "Dr" honorific prefix) —
  // "Dr. Sathya Reddy Kalahasti" → "Sathya", "Ananya Sharma" → "Ananya".
  const firstName = (n) => {
    if (!n) return "";
    const parts = n.trim().split(/\s+/).filter(Boolean);
    if (parts.length && /^dr\.?$/i.test(parts[0])) parts.shift();
    return parts[0] || "";
  };
  const spec = doc?.specialization || "";
  useEffect(() => {
    if (!doc) return;
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    document.title = `${withDr(doc.name)} · ${spec || "Neurologist"} · NeuroOpinion India`;
    const newDesc = `Get a written second opinion from ${withDr(doc.name)}, ${(spec || "neurologist").toLowerCase()} on NeuroOpinion India. ${doc.bio ? doc.bio.slice(0, 140) : "Verified specialist · 48-hour turnaround · Detailed written report."}`;
    if (meta) meta.setAttribute("content", newDesc);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [doc]);

  const startBooking = () => {
    if (!user) { nav(`/login?as=patient&next=/patient`); return; }
    if (user.role !== "patient") { toast.error("Switch to a patient account to book"); return; }
    nav(`/patient?doctor=${id}`);
  };

  const shareProfile = async () => {
    // OG story card URL — WhatsApp/Slack scrape this and render a rich
    // preview (title, description, doctor photo). Users still land on the
    // canonical /doctors/:id page via meta-refresh + JS redirect.
    const url = `${window.location.origin}/api/og/doctor/${id}`;
    const title = doc ? `${withDr(doc.name)} · NeuroOpinion India` : "NeuroOpinion India";
    const text = doc
      ? `Meet ${withDr(doc.name)} on NeuroOpinion India — ${spec || "verified neuro specialist"}. Get a written second opinion in 24–48 hours.`
      : "Get a verified neuro second opinion on NeuroOpinion India.";
    // Prefer native Web Share (mobile), fall back to WhatsApp intent or clipboard.
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (e) { /* user dismissed — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      toast.success("Profile link copied — paste it anywhere");
      setTimeout(() => setShared(false), 2500);
    } catch (e) {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank");
    }
  };

  const shareOnWhatsApp = () => {
    const url = `${window.location.origin}/api/og/doctor/${id}`;
    const text = doc
      ? `Meet ${withDr(doc.name)} on NeuroOpinion India — ${spec || "verified neuro specialist"}. Get a written second opinion in 24–48 hours: ${url}`
      : `Verified neuro second opinion in 24–48 hours: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (!doc) return <DoctorProfileSkeleton />;

  // Normalise alma_mater / education / expertise / languages — backend
  // may return any of: array, comma-separated string, or null.
  const _toList = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v.filter(Boolean);
    if (typeof v === "string") return v.split(",").map((s) => s.trim()).filter(Boolean);
    return [];
  };
  const _alumniList = [..._toList(doc.alma_mater), ..._toList(doc.education)];
  const _expertise = _toList(doc.expertise);
  const _languages = _toList(doc.languages);
  const consults = doc.consultations_count || 0;
  const consultsDisplay = consults >= 10000 ? `${Math.floor(consults / 1000)}K+`
    : consults >= 1000 ? `${(consults / 1000).toFixed(1)}K+`
    : consults.toLocaleString();

  // Doctor Person schema — emits structured JSON-LD so each doctor profile
  // can show as a rich Google knowledge card (photo, role, alma mater, ratings).
  const doctorSchema = {
    "@context": "https://schema.org",
    "@type": "Physician",
    "name": withDr(doc.name),
    "image": photoOf(doc) || "https://neuroopinion.in/icons/icon-512.png",
    "url": `https://neuroopinion.in/doctors/${id}`,
    "medicalSpecialty": spec || "Neurology",
    "jobTitle": spec || "Neurologist",
    "worksFor": {
      "@type": "MedicalOrganization",
      "name": "NeuroOpinion India",
      "url": "https://neuroopinion.in",
    },
    "alumniOf": _alumniList.map((m) => ({
      "@type": "EducationalOrganization",
      "name": typeof m === "string" ? m : (m.name || m.school || ""),
    })).filter((m) => m.name),
    "knowsAbout": _expertise.length ? _expertise : ["Brain tumour", "Stroke", "Epilepsy", "Spine", "Headache"],
    "knowsLanguage": _languages.length ? _languages : ["English", "Hindi"],
    "telephone": "+91-94417-24072",
    "areaServed": "India",
    "availableService": {
      "@type": "MedicalProcedure",
      "name": "Online second opinion",
      "procedureType": "Tele-consultation",
    },
    ...(doc.identifier ? { "identifier": doc.identifier } : {}),
    ...(consults > 0 ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": doc.rating || 4.8,
        "ratingCount": Math.max(consults, 5),
        "bestRating": 5,
        "worstRating": 1,
      },
    } : {}),
  };

  // Prune empty arrays so JSON-LD validators don't complain
  if (!doctorSchema.alumniOf?.length) delete doctorSchema.alumniOf;

  const photo = photoOf(doc);

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-10">
      {/* Doctor Person schema for Google rich snippets */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(doctorSchema) }}
      />
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">

        {/* Breadcrumb */}
        <div className="text-xs text-slate-500 mb-4">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <span className="mx-1.5">/</span>
          <span>Specialists</span>
          <span className="mx-1.5">/</span>
          <span className="text-slate-900">{doc.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main card */}
          <Card className="lg:col-span-2 overflow-hidden border-slate-200 rounded-2xl">
            {/* Premium teal cover bar — short, decorative */}
            <div className="relative h-20 sm:h-24 bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500" aria-hidden>
              <div className="absolute inset-0 opacity-[0.15] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:18px_18px]" />
            </div>
            <div className="p-6 sm:p-8">
              {/* Photo + name row — photo sits ABOVE the cover, fully visible */}
              <div className="flex items-end gap-5 flex-wrap -mt-16 sm:-mt-20">
                {photo ? (
                  <img
                    src={photo}
                    alt={doc.name}
                    className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl object-cover object-top ring-4 ring-white bg-slate-100 shadow-xl shrink-0"
                    data-testid="profile-photo"
                  />
                ) : (
                  <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 text-white grid place-items-center ring-4 ring-white shadow-xl shrink-0">
                    <Stethoscope className="w-12 h-12"/>
                  </div>
                )}
                <div className="flex-1 min-w-0 pb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Founder badge removed — `is_featured` used only for ordering */}
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                      <BadgeCheck className="w-3 h-3"/> Verified doctor
                    </span>
                    {doc.is_available !== false && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500 text-white inline-flex items-center gap-1.5"
                        data-testid="profile-live-badge"
                        title="Accepting new cases today"
                      >
                        <span className="relative flex w-1.5 h-1.5">
                          <span className="absolute inset-0 rounded-full bg-white opacity-75 animate-ping"/>
                          <span className="relative rounded-full w-1.5 h-1.5 bg-white"/>
                        </span>
                        Live · Available now
                      </span>
                    )}
                  </div>
                  <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-2">{doc.name}</h1>
                  <div className="text-sm text-slate-600 mt-0.5">{doc.specialization} · {doc.qualifications || "MBBS, MD"}</div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={shareProfile}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700"
                      data-testid="profile-share-btn"
                      aria-label="Share this specialist"
                    >
                      {shared ? <Check className="w-3.5 h-3.5 text-emerald-600"/> : <Share2 className="w-3.5 h-3.5"/>}
                      {shared ? "Link copied" : "Share profile"}
                    </button>
                    <button
                      onClick={shareOnWhatsApp}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-green-200 bg-green-50 hover:bg-green-100 text-xs font-medium text-green-700"
                      data-testid="profile-share-wa-btn"
                    >
                      <MessageCircle className="w-3.5 h-3.5"/> Share on WhatsApp
                    </button>
                  </div>
                </div>
              </div>

              {/* Intro video — auto-rendered when the doctor has uploaded one */}
              {doc.intro_video_url && (
                <div className="mt-6 rounded-2xl overflow-hidden bg-slate-900 shadow-lg border border-slate-200" data-testid="profile-intro-video">
                  <video
                    src={doc.intro_video_url.startsWith("http") ? doc.intro_video_url : `${BACKEND}${doc.intro_video_url}`}
                    controls
                    playsInline
                    preload="metadata"
                    poster={photo || undefined}
                    className="w-full aspect-video bg-black"
                  />
                  <div className="px-4 py-2.5 bg-slate-900/95 text-xs text-slate-300 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"/>
                    A short intro from {doc.name?.split(" ")[0] || "your specialist"}
                  </div>
                </div>
              )}

              {/* Stat strip */}
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <ProfileStat icon={Star}      value={doc.rating ? doc.rating.toFixed(1) : "—"} label="Patient rating" accent="text-amber-600" />
                <ProfileStat icon={Users}     value={consultsDisplay || "—"} label="Consultations" />
                <ProfileStat icon={Award}     value={`${doc.experience_years || 0} yrs`} label="Experience" />
                <ProfileStat icon={ShieldCheck} value="MCI/NMC" label="Verified" />
              </div>

              {/* About */}
              <div className="mt-8">
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0B5ED7]">About</div>
                <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-line">
                  {doc.bio || "Practising specialist on the NeuroOpinion India network."}
                </p>
              </div>

              {/* Expertise */}
              <div className="mt-8">
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0B5ED7]">Expertise</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(doc.expertise || ["Brain tumour", "Stroke", "Epilepsy", "Spine", "Headache", "Pediatric"]).map((e) => (
                    <span key={e} className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full border border-slate-200">
                      {e}
                    </span>
                  ))}
                </div>
              </div>

              {/* Languages + privacy + misc */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InfoRow icon={Languages} label="Languages" value="English · Telugu · Hindi" />
                <InfoRow icon={ShieldCheck} label="Privacy" value="HIPAA-aligned · DPDP compliant" />
                <InfoRow icon={Clock} label="Response time" value="Standard 24–48 hrs · Priority <24 hrs" />
                <InfoRow icon={Award} label="Registration" value={doc.registration_number || "MCI/NMC (verified)"} />
              </div>
            </div>
          </Card>

          {/* Booking sidecard */}
          <Card className="p-6 border-slate-200 h-fit lg:sticky lg:top-24 bg-white hidden md:block">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">Written second opinion</div>
            <div className="mt-1 font-heading text-2xl font-bold text-slate-900">Book with {firstName(doc.name) || "this specialist"}</div>
            <div className="text-sm text-slate-500">Detailed written report · 48-hr turnaround</div>

            <div className="mt-4 inline-flex items-center gap-1 text-[11px] font-semibold text-[#20C997] bg-[#20C997]/10 px-2.5 py-1 rounded-full border border-[#20C997]/20">
              <span className="w-1.5 h-1.5 rounded-full bg-[#20C997] animate-pulse"/> Available today
            </div>

            <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2">
              <Zap className="w-4 h-4 mt-0.5"/>
              <div>
                <div className="font-semibold">Need it prioritised?</div>
                Choose <b>Priority or Video Consultation</b> at booking — priority queue, &lt;24 hrs
              </div>
            </div>

            <Button onClick={startBooking} className="mt-5 w-full h-12 bg-[#0B5ED7] hover:bg-[#084BB0] text-white" data-testid="profile-book-btn">
              Book consultation <ArrowRight className="w-4 h-4 ml-2"/>
            </Button>

            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              🔒 Encrypted scan storage · Written report · 100% refund if the doctor cannot accept.
            </p>

            <div className="mt-5 pt-4 border-t border-slate-100 space-y-2">
              <a
                href="https://wa.me/918317674160"
                target="_blank" rel="noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm text-slate-700"
              >
                <MessageCircle className="w-4 h-4 text-green-600"/> Ask on WhatsApp
              </a>
            </div>
          </Card>
        </div>

        {/* Patient reviews — verified reviews from completed cases */}
        {reviewsBlock.items.length > 0 && (
          <div className="mt-12" data-testid="profile-reviews-section">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-emerald-700">Verified patient reviews</div>
                <h2 className="mt-2 font-heading text-2xl font-semibold text-slate-900">
                  What patients say about {firstName(doc.name) || "this specialist"}
                </h2>
              </div>
              {reviewsBlock.review_count > 0 && (
                <div className="inline-flex items-center gap-2 text-sm">
                  <div className="flex items-center gap-0.5 text-amber-500">
                    {[0,1,2,3,4].map(i => <Star key={i} className={`w-4 h-4 ${i < Math.round(reviewsBlock.avg_rating) ? "fill-amber-500" : ""}`}/>)}
                  </div>
                  <span className="font-heading font-bold text-slate-900">{reviewsBlock.avg_rating.toFixed(1)}</span>
                  <span className="text-slate-500">· {reviewsBlock.review_count} review{reviewsBlock.review_count !== 1 ? "s" : ""}</span>
                </div>
              )}
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviewsBlock.items.map((r, i) => (
                <Card key={i} className="p-5 border-slate-200 bg-white" data-testid={`profile-review-${i}`}>
                  <Quote className="w-5 h-5 text-emerald-600/60"/>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed line-clamp-6">&ldquo;{r.text}&rdquo;</p>
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-slate-900">{r.patient_name_anon || "Verified patient"}</div>
                    <div className="flex items-center gap-0.5 text-amber-500">
                      {Array.from({ length: r.rating || 5 }).map((_, j) => <Star key={j} className="w-3 h-3 fill-amber-500"/>)}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Other specialists */}
        <div className="mt-12">
          <h2 className="font-heading text-2xl font-semibold text-slate-900">Other specialists</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {doctors.filter(d => d.id !== id).slice(0, 6).map(d => {
              const p = photoOf(d);
              return (
                <Link key={d.id} to={`/doctors/${d.id}`}>
                  <Card className="p-5 border-slate-200 hover:shadow-md hover:border-[#0B5ED7]/40 transition" data-testid={`other-doc-${d.id}`}>
                    <div className="flex items-center gap-3">
                      {p ? (
                        <img src={p} alt={d.name} className="w-12 h-12 rounded-xl object-cover ring-2 ring-slate-100 bg-slate-50"/>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-[#0B5ED7]/10 grid place-items-center text-[#0B5ED7]"><Stethoscope className="w-6 h-6"/></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-semibold text-slate-900 truncate flex items-center gap-1">
                          {d.name} <BadgeCheck className="w-3.5 h-3.5 text-[#0B5ED7] shrink-0"/>
                        </div>
                        <div className="text-xs text-slate-500">{d.specialization} · {d.experience_years}+ yrs</div>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile sticky book bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-slate-200 p-3 flex items-center gap-3 shadow-lg">
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-base text-slate-900 truncate">Written second opinion</div>
          <div className="text-[11px] text-slate-500">Detailed report · 48 hrs</div>
        </div>
        <Button onClick={startBooking} className="h-11 px-5 bg-[#0B5ED7] hover:bg-[#084BB0] text-white" data-testid="profile-book-sticky-btn">
          <Phone className="w-4 h-4 mr-2"/> Book now
        </Button>
      </div>
    </div>
  );
}

function ProfileStat({ icon: Icon, value, label, accent = "text-[#0B5ED7]" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-center">
      <Icon className={`w-4 h-4 mx-auto ${accent}`}/>
      <div className="mt-1.5 font-heading font-bold text-slate-900 text-lg leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 grid place-items-center text-[#0B5ED7] shrink-0">
        <Icon className="w-4 h-4"/>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-sm font-medium text-slate-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

// Skeleton placeholder while the doctor profile loads — looks premium vs spinner
function DoctorProfileSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-pulse">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 overflow-hidden border-slate-200">
            <div className="h-32 bg-slate-200"/>
            <div className="p-8 -mt-16">
              <div className="flex items-end gap-4">
                <div className="w-28 h-28 rounded-2xl bg-slate-200 ring-4 ring-white"/>
                <div className="flex-1 pb-2 space-y-2">
                  <div className="h-3 w-24 bg-slate-200 rounded"/>
                  <div className="h-6 w-56 bg-slate-300 rounded"/>
                  <div className="h-3 w-40 bg-slate-200 rounded"/>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-4 gap-3">
                {[1,2,3,4].map(i => <div key={i} className="h-16 rounded-xl bg-slate-200"/>)}
              </div>
              <div className="mt-8 space-y-2">
                <div className="h-3 w-24 bg-slate-200 rounded"/>
                <div className="h-3 w-full bg-slate-200 rounded"/>
                <div className="h-3 w-11/12 bg-slate-200 rounded"/>
                <div className="h-3 w-4/5 bg-slate-200 rounded"/>
              </div>
            </div>
          </Card>
          <Card className="p-6 h-fit border-slate-200 space-y-3 hidden md:block">
            <div className="h-3 w-20 bg-slate-200 rounded"/>
            <div className="h-10 w-32 bg-slate-300 rounded"/>
            <div className="h-12 w-full bg-slate-200 rounded-lg mt-4"/>
            <div className="h-10 w-full bg-slate-200 rounded-lg"/>
          </Card>
        </div>
      </div>
    </div>
  );
}
